import { httpsCallable, type HttpsCallable } from 'firebase/functions';
import { z } from 'zod';

import { functions } from '../firebase.js';
import { SchemaValidationFailedError } from '@kilat/shared';

/**
 * Sprint 82 — typed wrappers around the super-admin recruiter
 * moderation callables. Both functions live in
 * `apps/functions/src/recruiters/` and are gated server-side to
 * `isSuperAdmin()`. Client validation here is purely a fast-feedback
 * convenience — the server re-validates with its own Zod schemas.
 */

// ── approveRecruiterApplication ─────────────────────────────────────────

export const ApproveRecruiterApplicationInputSchema = z.object({
  applicationId: z.string().min(1),
});
export type ApproveRecruiterApplicationInput = z.infer<
  typeof ApproveRecruiterApplicationInputSchema
>;

export const ApproveRecruiterApplicationOutputSchema = z.object({
  recruiterId: z.string(),
  code: z.string().regex(/^RC-[A-Z0-9]{4,6}$/),
  signInToken: z.string(),
});
export type ApproveRecruiterApplicationOutput = z.infer<
  typeof ApproveRecruiterApplicationOutputSchema
>;

const approveFn: HttpsCallable<
  ApproveRecruiterApplicationInput,
  ApproveRecruiterApplicationOutput
> = httpsCallable(functions, 'approveRecruiterApplication');

export async function callApproveRecruiterApplication(
  input: ApproveRecruiterApplicationInput,
): Promise<ApproveRecruiterApplicationOutput> {
  const parsed = ApproveRecruiterApplicationInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError(
      'approveRecruiterApplication.input',
      parsed.error.flatten(),
    );
  }
  const result = await approveFn(parsed.data);
  const out = ApproveRecruiterApplicationOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError(
      'approveRecruiterApplication.output',
      out.error.flatten(),
    );
  }
  return out.data;
}

// ── rejectRecruiterApplication ──────────────────────────────────────────

export const RejectRecruiterApplicationInputSchema = z.object({
  applicationId: z.string().min(1),
  reason: z.string().max(500).optional(),
});
export type RejectRecruiterApplicationInput = z.infer<typeof RejectRecruiterApplicationInputSchema>;

export const RejectRecruiterApplicationOutputSchema = z.object({
  applicationId: z.string(),
});
export type RejectRecruiterApplicationOutput = z.infer<
  typeof RejectRecruiterApplicationOutputSchema
>;

const rejectFn: HttpsCallable<RejectRecruiterApplicationInput, RejectRecruiterApplicationOutput> =
  httpsCallable(functions, 'rejectRecruiterApplication');

export async function callRejectRecruiterApplication(
  input: RejectRecruiterApplicationInput,
): Promise<RejectRecruiterApplicationOutput> {
  const parsed = RejectRecruiterApplicationInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError(
      'rejectRecruiterApplication.input',
      parsed.error.flatten(),
    );
  }
  const result = await rejectFn(parsed.data);
  const out = RejectRecruiterApplicationOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('rejectRecruiterApplication.output', out.error.flatten());
  }
  return out.data;
}
